<script setup>
import {inject, onBeforeMount, ref} from 'vue';
import {Head, router} from '@inertiajs/vue3';
import StoreLayout from '@/Layouts/StoreLayout.vue';
import Browse from "@/Pages/Product/Partials/Browse.vue";
import Grid from "@/Pages/Product/Partials/Grid.vue";
import {PhX, PhCopySimple, PhSpinnerGap, PhSquaresFour} from '@phosphor-icons/vue';
import {imageSize} from '@/mixins';
import PrimaryButton from "@/Components/PrimaryButton.vue";
import Empty from "@/Components/Empty.vue";
import ShopLink from "@/Components/ShopLink.vue";

const transformer = ref(null);
const saving = ref(false);

const props = defineProps({
    title: String,
    products: Object,
});

const route = inject('route');
const configKonva = ref({});

let selectedShapeName = ref('');

const rectangles = ref([]);

function submit(rect, preserveState = true) {
    router.patch(route('design-wall.patch'), {
        productId: rect.productId,
        x: rect.x,
        y: rect.y,
        scale: rect.scaleX,
        rotation: rect.rotation,
        instance: rect.instance,
    }, {
        preserveScroll: true,
        preserveState: preserveState,
        onStart: () => saving.value = true,
        onFinish: () => saving.value = false,
    });
}

function handleTransformEnd(e) {
    // shape is transformed, let us save new attrs back to the node
    // find element in our state
    const rect = rectangles.value.find(
        (r) => r.name === selectedShapeName.value
    );
    // update the state
    if (rect) {
        rect.x = e.target.x();
        rect.y = e.target.y();
        rect.rotation = e.target.rotation();
        rect.scaleX = e.target.scaleX();
        rect.scaleY = e.target.scaleY();
        submit(rect);
    }
}

function handleStageMouseDown(e) {
    // clicked on stage - clear selection
    if (e.target === e.target.getStage()) {
        selectedShapeName.value = '';
        updateTransformer();
        return;
    }

    // clicked on transformer - do nothing
    const clickedOnTransformer =
        e.target.getParent().className === 'Transformer';
    if (clickedOnTransformer) {
        return;
    }

    // find clicked rect by its name
    const name = e.target.name();
    const rect = rectangles.value.find((r) => r.name === name);
    if (rect) {
        selectedShapeName.value = name;
    } else {
        selectedShapeName.value = '';
    }
    updateTransformer();
}

function updateTransformer() {
    // here we need to manually attach or detach Transformer node
    const transformerNode = transformer.value.getNode();
    const stage = transformerNode.getStage();

    const selectedNode = stage.findOne('.' + selectedShapeName.value);
    // do nothing if selected node is already attached
    if (selectedNode === transformerNode.node()) {
        return;
    }

    if (selectedNode) {
        // attach to another node
        transformerNode.nodes([selectedNode]);
    } else {
        // remove transformer
        transformerNode.nodes([]);
    }
}

const menu = ref(null);

function handleContextMenu(e) {
    e.evt.preventDefault();
    if (e.target instanceof Konva.Stage || selectedShapeName.value === '') {
        // Do nothing
        return;
    }
    let currentShape = e.target;
    menu.value.style.display = 'initial';
    let stage = currentShape.getStage();
    let pos = stage.getRelativePointerPosition();
    menu.value.style.top = pos.y + 4 + 'px';
    menu.value.style.left = pos.x + 4 + 'px';
}

function deleteShape() {
    const rect = rectangles.value.find(
        (r) => r.name === selectedShapeName.value
    );
    router.post(route('design-wall.delete'), {
        productId: rect.productId,
        instance: rect.instance,
    }, {
        preserveScroll: true,
        preserveState: false,
    });
}

function duplicateShape() {
    const rect = rectangles.value.find(
        (r) => r.name === selectedShapeName.value
    );
    const newRect = {...rect};
    newRect.instance = rect.nextIndex;
    newRect.name = newRect.ItemCode + "-" + (rect.nextIndex + 1);
    newRect.x = rect.x - 10;
    newRect.y = rect.y - 10;

    // rectangles.value.push(newRect);
    // selectedShapeName.value = ''; // newRect.name;
    // updateTransformer();

    submit(newRect, false);
}

onBeforeMount(() => {
    rectangles.value = props.products.data.slice().reverse().flatMap((product) => {
        return product.instances.map((instance, index) => {
            const image = new window.Image();
            image.src = imageSize(product.imageSrc, 500);
            let scale = 1.0;
            let rotation = 0;
            const x = instance[0];
            const y = instance[1];
            if (instance.length > 3) {
                scale = instance[3];
                rotation = instance[4];
            }
            return {
                rotation,
                x,
                y,
                width: 124,
                height: 124,
                scaleX: scale,
                scaleY: scale,
                name: product.id + "-" + (index + 1),
                draggable: true,
                image: image,
                itemCode: product.id,
                productId: product.productId,
                instance: index,
                nextIndex: product.instances.length,
            };
        })
    });
    configKonva.value = {
        width: window.innerWidth,
        height: 500,
    };
    window.addEventListener('click', () => {
        // hide menu
        if (menu.value != null) {
            menu.value.style.display = 'none';
        }
    });
});

</script>

<template>
    <Head :title="title"/>

    <StoreLayout>
        <Browse :title="title" :filters="[]" :sortOptions="[]" :active-filters="[]">
            <template v-if="products.data.length">
            <div class="relative border border-gray-300 mb-4 overflow-hidden">
                <v-stage :config="configKonva"
                         @mousedown="handleStageMouseDown"
                         @touchstart="handleStageMouseDown"
                         @contextmenu="handleContextMenu">
                    <v-layer ref="layer">
                        <v-image
                            v-for="image in rectangles"
                            :key="image.id"
                            :config="image"
                            @dragend="handleTransformEnd"
                            @transformend="handleTransformEnd"
                            @dblclick="duplicateShape"
                        />
                        <v-transformer ref="transformer"/>
                    </v-layer>
                </v-stage>
                <PhSpinnerGap v-if="saving"
                              class="animate-spin w-8 h-8 absolute top-2 left-2"/>
                <div class="absolute hidden" ref="menu">
                    <div
                        class="bg-white w-40 border border-gray-300 rounded-lg flex flex-col text-sm py-4 px-2 text-gray-500 shadow-lg">
                        <div class="flex cursor-pointer hover:bg-gray-100 py-1 px-2 rounded"
                             @click="duplicateShape">
                            <PhCopySimple class="h-4 w-4"/>
                            <div class="ml-2">Duplicate</div>
                        </div>
                        <div class="flex cursor-pointer hover:bg-gray-100 py-1 px-2 rounded" @click="deleteShape">
                            <PhX class="h-4 w-4"/>
                            <div class="ml-2">Delete</div>
                        </div>
                    </div>
                </div>
                <div class="w-full h-16 bg-gray-100 flex items-center px-4">
                    <div v-if="selectedShapeName.length" class="space-x-2">
                        Selected {{ selectedShapeName.replace('-', ' ') }}
                        <PrimaryButton @click="deleteShape" class="ml-2 !py-1">
                            <PhX class="h-5 w-5 mr-1" weight="bold" /> Delete
                        </PrimaryButton>
                        <PrimaryButton :inverted="true" class="!py-1" @click="duplicateShape">
                            <PhCopySimple class="h-5 w-5 mr-1" weight="bold" />
                            Duplicate
                        </PrimaryButton>
                    </div>
                    <p v-else>
                        Click to select a swatch and transform. Double-click to duplicate a swatch.
                    </p>
                </div>
            </div>
            </template>
            <div v-else class="max-w-4xl mx-auto px-6 py-12 lg:px-0">
                <Empty :icon="PhSquaresFour"
                       title="Your design wall is empty"
                       description="Add fabrics to your design wall to visualize them together.">
                    <ShopLink :href="route('products.browse')">Start browsing</ShopLink>
                </Empty>
            </div>
            <Grid v-if="products.data.length" :products="products"/>
        </Browse>
    </StoreLayout>
</template>
